import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2, Scale, Play, Lock, CheckCircle2, Search, Sparkles, Printer, RotateCcw, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import axios from "axios";

const AUBERGINE = "#5A4565";
const CREAM = "#FBF6EA";
const EDGE = "#EFE4CC";
const GREEN = "#2F4A2A";
const BERRY = "#6E1E3A";
const GOLD = "#C49247";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const input = { borderColor: EDGE, backgroundColor: CREAM };

export default function AdminFairsale() {
  const [releases, setReleases] = useState([]);
  const [selected, setSelected] = useState(null); // release id
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/console/fairsale/releases");
      setReleases(r.data.releases || []);
      if (!selected && r.data.releases?.length) setSelected(r.data.releases[0].id);
    } catch { toast.error("Couldn't load releases."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div data-testid="admin-fairsale" className="space-y-6" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: BERRY }}>✦ Fair allocation sale</div>
        <h1 className="text-4xl mt-1 leading-tight">the Fairitage Tuber Sale</h1>
        <p className="italic mt-1 text-sm opacity-70">
          Set up a release, open the window, watch demand, then run the allocation , VIPs first, then a fair shuffle.
        </p>
      </header>

      <CreateRelease onCreated={(r) => { setSelected(r.id); load(); }} />

      <TestRunBanner onCreated={(rid) => { setSelected(rid); load(); }} />

      {loading ? (
        <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : releases.length === 0 ? (
        <div className="bg-white border rounded-md p-8 text-center italic opacity-60" style={{ borderColor: EDGE }} data-testid="fairsale-empty">
          No releases yet , create your first one above.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {releases.map((r) => (
              <button key={r.id} type="button" onClick={() => setSelected(r.id)} data-testid={`release-pill-${r.id}`}
                className="px-4 h-9 rounded-full text-xs font-semibold border-2"
                style={selected === r.id ? { backgroundColor: AUBERGINE, borderColor: AUBERGINE, color: "#fff" } : { borderColor: EDGE, backgroundColor: "#fff", color: AUBERGINE }}>
                {r.name} · {r.status}{r.wishlist_count ? ` · ${r.wishlist_count} lists` : ""}
              </button>
            ))}
          </div>
          {selected && <ReleaseDetail rid={selected} onChanged={load} />}
        </>
      )}
    </div>
  );
}

function CreateRelease({ onCreated }) {
  const [form, setForm] = useState({ name: "", closes_at: "", max_wishes: 15, max_tubers: 10, payment_window_hours: 48 });
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Give the release a name."); return; }
    setBusy(true);
    try {
      const closes = form.closes_at ? new Date(form.closes_at).toISOString() : "";
      const r = await api.post("/console/fairsale/releases", { ...form, closes_at: closes });
      toast.success("Release created as a draft , add varieties then open it.");
      setForm({ name: "", closes_at: "", max_wishes: 15, max_tubers: 10, payment_window_hours: 48 });
      onCreated(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't create."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={create} className="bg-white border rounded-md p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end" style={{ borderColor: EDGE }}>
      <div className="col-span-2">
        <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">Release name</label>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Spring 2026 Release" className="mt-1 w-full h-10 px-3 border rounded" style={input} data-testid="release-name" />
      </div>
      <div>
        <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">Sale closes</label>
        <input type="datetime-local" value={form.closes_at} onChange={(e) => setForm({ ...form, closes_at: e.target.value })} className="mt-1 w-full h-10 px-2 border rounded text-xs" style={input} data-testid="release-closes" />
      </div>
      <NumField label="Max wishes" v={form.max_wishes} set={(v) => setForm({ ...form, max_wishes: v })} testid="release-max-wishes" />
      <NumField label="Max tubers" v={form.max_tubers} set={(v) => setForm({ ...form, max_tubers: v })} testid="release-max-tubers" />
      <div className="flex gap-2 items-end">
        <NumField label="Pay hrs" v={form.payment_window_hours} set={(v) => setForm({ ...form, payment_window_hours: v })} testid="release-pay-hours" />
        <button type="submit" disabled={busy} className="h-10 px-4 rounded text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50" style={{ backgroundColor: BERRY }} data-testid="release-create">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
        </button>
      </div>
    </form>
  );
}

function TestRunBanner({ onCreated }) {
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/console/fairsale/test-run");
      toast.success(`Practice sale "${r.data.name}" created with ${r.data.wishlists} pretend wishlists. Hit "Run allocation (preview)" to watch the engine work.`);
      onCreated(r.data.release_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create the test run."); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-dashed rounded-md p-3.5 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: GOLD }} data-testid="fairsale-test-banner">
      <p className="text-xs italic opacity-75 flex-1 min-w-[240px]">
        Want to practise first? Create a throwaway test sale with sample varieties and six pretend
        customers, preview the allocation, then delete it. Confirming would create practice orders
        for @example.com customers only.
      </p>
      <button type="button" onClick={create} disabled={busy} data-testid="fairsale-test-run"
        className="h-9 px-4 rounded text-white text-xs tracking-[0.15em] uppercase font-semibold inline-flex items-center gap-1.5"
        style={{ backgroundColor: GOLD }}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />} Create test run
      </button>
    </div>
  );
}

function NumField({ label, v, set, testid }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">{label}</label>
      <input type="number" min="1" value={v} onChange={(e) => set(parseInt(e.target.value || "1", 10))} className="mt-1 w-full h-10 px-2 border rounded" style={input} data-testid={testid} />
    </div>
  );
}

function ReleaseDetail({ rid, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/console/fairsale/releases/${rid}`);
      setData(r.data);
    } catch { toast.error("Couldn't load release."); }
  };
  useEffect(() => { setPreview(null); load(); }, [rid]); // eslint-disable-line
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/catalog`).then((r) => {
      const rows = Array.isArray(r.data) ? r.data : r.data.varieties || [];
      setCatalog(rows);
    }).catch(() => {});
  }, []);

  const release = data?.release;
  const items = release?.items || [];
  const inRelease = useMemo(() => new Set(items.map((i) => i.variety_id)), [items]);
  const matches = useMemo(() => {
    if (!q.trim()) return [];
    const n = q.toLowerCase();
    return catalog.filter((v) => v.name?.toLowerCase().includes(n) && !inRelease.has(v.id)).slice(0, 6);
  }, [q, catalog, inRelease]);

  const saveItems = async (next) => {
    setBusy("items");
    try {
      await api.patch(`/console/fairsale/releases/${rid}`, { items: next });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save varieties."); }
    finally { setBusy(""); }
  };

  const addVariety = (v, tier) => {
    saveItems([...items, {
      variety_id: v.id, name: v.name, image: v.image || "", description: v.description || "",
      colour: v.colour || "", height: v.height || "", tier, price: tier === "A" ? 35 : 15, stock: 5,
    }]);
    setQ("");
  };

  const patchItem = (vid, p) => saveItems(items.map((i) => (i.variety_id === vid ? { ...i, ...p } : i)));
  const removeItem = (vid) => saveItems(items.filter((i) => i.variety_id !== vid));

  const setStatus = async (status) => {
    setBusy("status");
    try {
      await api.patch(`/console/fairsale/releases/${rid}`, { status });
      toast.success(status === "open" ? "Sale is OPEN , customers can build their lists." : `Release ${status}.`);
      await load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
    finally { setBusy(""); }
  };

  const allocate = async (confirm) => {
    setBusy("allocate");
    try {
      const r = await api.post(`/console/fairsale/releases/${rid}/allocate`, { confirm });
      if (confirm) {
        toast.success(`Allocation confirmed , ${r.data.summary.tubers_allocated} tubers across ${r.data.summary.customers_allocated} customers. Orders created.`);
        setPreview(null);
        await load(); onChanged();
      } else {
        setPreview(r.data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Allocation failed."); }
    finally { setBusy(""); }
  };

  const waitlistRound = async () => {
    setBusy("waitlist");
    try {
      const r = await api.post(`/console/fairsale/releases/${rid}/waitlist-round`);
      const d = r.data;
      if (!d.forfeited.length && !d.reallocated.length) {
        toast.info("Nothing to do , no overdue unpaid allocations and/or no one waiting.");
      } else {
        toast.success(`Waitlist round done , ${d.forfeited.length} forfeited, ${d.reallocated.length} customers re-allocated (${d.tubers} tubers, ${money(d.revenue)}).`);
      }
      await load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Waitlist round failed."); }
    finally { setBusy(""); }
  };

  const printPickingList = async () => {
    setBusy("print");
    try {
      const r = await api.get(`/console/fairsale/releases/${rid}/picking-list`);
      openPickingList(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build the picking list."); }
    finally { setBusy(""); }
  };

  if (!release) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const allocated = release.status === "allocated";

  return (
    <div className="space-y-5" data-testid="release-detail">
      <div className="bg-white border rounded-md p-4 flex flex-wrap items-center gap-3 justify-between" style={{ borderColor: EDGE }}>
        <div>
          <span className="font-semibold text-lg">{release.name}</span>
          <span className="ml-3 px-2.5 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold text-white"
            style={{ backgroundColor: allocated ? GREEN : release.is_open ? GOLD : AUBERGINE }} data-testid="release-status">
            {allocated ? "Allocated" : release.is_open ? "Open" : release.status}
          </span>
          {release.closes_at && <span className="ml-3 text-xs opacity-60">Closes {new Date(release.closes_at).toLocaleString()}</span>}
          <span className="ml-3 text-xs opacity-60">{release.max_wishes} wishes / {release.max_tubers} tubers / {release.payment_window_hours}h to pay</span>
        </div>
        {allocated && (
          <div className="flex gap-2">
            <button type="button" onClick={printPickingList} disabled={busy === "print"} data-testid="release-picking-list"
              className="h-9 px-4 rounded text-white text-xs tracking-[0.15em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ backgroundColor: AUBERGINE }}>
              {busy === "print" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} Picking list
            </button>
            <button type="button" onClick={waitlistRound} disabled={busy === "waitlist"} data-testid="release-waitlist-round"
              className="h-9 px-4 rounded text-white text-xs tracking-[0.15em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ backgroundColor: BERRY }}>
              {busy === "waitlist" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Waitlist round
            </button>
          </div>
        )}
        {!allocated && (
          <div className="flex gap-2">
            {release.status !== "open" && (
              <button type="button" onClick={() => setStatus("open")} disabled={busy === "status" || !items.length} data-testid="release-open"
                className="h-9 px-4 rounded text-white text-xs tracking-[0.15em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" style={{ backgroundColor: GREEN }}>
                <Play className="w-3.5 h-3.5" /> Open sale
              </button>
            )}
            {release.status === "open" && (
              <button type="button" onClick={() => setStatus("closed")} disabled={busy === "status"} data-testid="release-close"
                className="h-9 px-4 rounded text-white text-xs tracking-[0.15em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ backgroundColor: BERRY }}>
                <Lock className="w-3.5 h-3.5" /> Close sale
              </button>
            )}
            {release.status === "closed" && (
              <button type="button" onClick={() => allocate(false)} disabled={busy === "allocate"} data-testid="release-preview-allocation"
                className="h-9 px-4 rounded text-white text-xs tracking-[0.15em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ backgroundColor: BERRY }}>
                {busy === "allocate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scale className="w-3.5 h-3.5" />} Run allocation (preview)
              </button>
            )}
          </div>
        )}
      </div>

      {allocated && release.allocation_summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="allocation-summary">
          <Tile label="Customers allocated" value={release.allocation_summary.customers_allocated} />
          <Tile label="Tubers allocated" value={release.allocation_summary.tubers_allocated} />
          <Tile label="Allocation revenue" value={money(release.allocation_summary.revenue)} />
          <Tile label="Wishlists entered" value={release.allocation_summary.customers} />
        </div>
      )}

      {allocated && (release.waitlist_rounds || []).length > 0 && (
        <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid="waitlist-rounds">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold opacity-70 mb-2">Waitlist rounds</div>
          <ul className="text-sm space-y-1">
            {release.waitlist_rounds.map((wr, i) => (
              <li key={wr.at || i} className="flex flex-wrap gap-x-3 gap-y-0.5">
                <span className="opacity-60">{new Date(wr.at).toLocaleString()}</span>
                <span>{wr.forfeited} forfeited → {wr.reallocated} re-allocated · {wr.tubers} tubers · {money(wr.revenue)}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] italic opacity-60 mt-2">
            Runs automatically every 30 minutes once payment windows lapse , the button above forces it now.
          </p>
        </div>
      )}

      {preview && (
        <div className="bg-white border-2 rounded-md p-5 space-y-4" style={{ borderColor: GOLD }} data-testid="allocation-preview">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold text-lg">Allocation preview , nothing saved yet</h3>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPreview(null)} className="h-9 px-4 rounded border text-xs font-semibold" style={{ borderColor: AUBERGINE }} data-testid="allocation-cancel">Cancel</button>
              <button type="button" onClick={() => allocate(true)} disabled={busy === "allocate"} data-testid="allocation-confirm"
                className="h-9 px-4 rounded text-white text-xs tracking-[0.15em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ backgroundColor: GREEN }}>
                {busy === "allocate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Confirm &amp; create orders
              </button>
            </div>
          </div>
          <div className="text-sm opacity-75">
            {preview.summary.tubers_allocated} tubers → {preview.summary.customers_allocated}/{preview.summary.customers} customers · projected {money(preview.summary.revenue)}
          </div>
          <ul className="divide-y max-h-80 overflow-auto" style={{ borderColor: EDGE }}>
            {preview.results.map((r) => (
              <li key={r.email} className="py-2 text-sm flex flex-wrap gap-2 justify-between">
                <span>
                  {r.vip && <Sparkles className="w-3.5 h-3.5 inline mr-1" style={{ color: GOLD }} />}
                  <strong>{r.name || r.email}</strong> <span className="opacity-50">({r.picks_count} picks)</span>
                </span>
                <span className="opacity-80">{r.allocated.length ? `${r.allocated.map((a) => a.name).join(", ")} · ${money(r.total)}` : ", nothing available"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!allocated && (
        <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }}>
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold opacity-70 mb-2">Add varieties from the catalogue</div>
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search catalogue…" className="w-full h-10 pl-9 pr-3 border rounded" style={input} data-testid="variety-search" />
          </div>
          {matches.length > 0 && (
            <ul className="mt-2 border rounded divide-y max-w-md" style={{ borderColor: EDGE }}>
              {matches.map((v) => (
                <li key={v.id} className="p-2.5 flex items-center gap-3 text-sm" data-testid={`variety-match-${v.id}`}>
                  {v.image ? <img src={v.image} alt="" className="w-9 h-9 rounded object-cover" /> : <span className="w-9 h-9 rounded" style={{ backgroundColor: CREAM }} />}
                  <span className="flex-1 font-semibold">{v.name}</span>
                  <button type="button" onClick={() => addVariety(v, "A")} className="px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: BERRY }} data-testid={`variety-add-a-${v.id}`}>+ A tier</button>
                  <button type="button" onClick={() => addVariety(v, "B")} className="px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: AUBERGINE }} data-testid={`variety-add-b-${v.id}`}>+ B tier</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: EDGE }}>
        {items.length === 0 ? (
          <div className="p-8 text-center italic opacity-60" data-testid="items-empty">No varieties yet , search the catalogue above.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.25em] uppercase" style={{ backgroundColor: CREAM }}>
                <th className="px-4 py-2.5">Variety</th>
                <th className="px-3 py-2.5">Tier</th>
                <th className="px-3 py-2.5">Price</th>
                <th className="px-3 py-2.5">Stock</th>
                <th className="px-3 py-2.5">Wishes</th>
                <th className="px-3 py-2.5">1st choices</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {(data.demand || []).map((d) => (
                <tr key={d.variety_id} className="border-t" style={{ borderColor: EDGE, opacity: busy === "items" ? 0.6 : 1 }} data-testid={`item-row-${d.variety_id}`}>
                  <td className="px-4 py-2 font-semibold">{d.name}</td>
                  <td className="px-3 py-2">
                    {allocated ? d.tier : (
                      <select value={d.tier} onChange={(e) => patchItem(d.variety_id, { tier: e.target.value })} className="border rounded px-1.5 py-1" style={input} data-testid={`item-tier-${d.variety_id}`}>
                        <option value="A">A</option><option value="B">B</option>
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {allocated ? money(d.price) : (
                      <input type="number" step="0.5" min="0" value={d.price} onChange={(e) => patchItem(d.variety_id, { price: parseFloat(e.target.value || "0") })} className="w-20 border rounded px-1.5 py-1" style={input} data-testid={`item-price-${d.variety_id}`} />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {allocated ? `${d.stock} left` : (
                      <input type="number" min="0" value={d.stock} onChange={(e) => patchItem(d.variety_id, { stock: parseInt(e.target.value || "0", 10) })} className="w-16 border rounded px-1.5 py-1" style={input} data-testid={`item-stock-${d.variety_id}`} />
                    )}
                  </td>
                  <td className="px-3 py-2">{d.wishes}</td>
                  <td className="px-3 py-2">{d.first_choices}</td>
                  <td className="px-3 py-2 text-right">
                    {!allocated && (
                      <button type="button" onClick={() => removeItem(d.variety_id)} className="p-1.5 rounded hover:bg-red-50 text-red-700" data-testid={`item-remove-${d.variety_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(data.wishlists || []).length > 0 && (
        <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid="wishlists-panel">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold opacity-70 mb-2">Wishlists ({data.wishlists.length})</div>
          <ul className="divide-y text-sm" style={{ borderColor: EDGE }}>
            {data.wishlists.map((w) => (
              <li key={w.email} className="py-2 flex flex-wrap justify-between gap-2">
                <span><strong>{w.name || w.email}</strong> <span className="opacity-50">{w.email}</span></span>
                <span className="opacity-75">{w.picks_count} picks · {w.status}{w.allocated_count ? ` · ${w.allocated_count} allocated` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }}>
      <div className="text-[10px] tracking-[0.3em] uppercase font-semibold opacity-70">{label}</div>
      <div className="text-2xl mt-1 font-semibold" style={{ color: BERRY }}>{value}</div>
    </div>
  );
}

function openPickingList(data) {
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const varietyRows = data.by_variety.map((v) => `
    <tr><td><strong>${esc(v.name)}</strong> <span class="tier">${esc(v.tier)}</span></td>
    <td class="qty">${v.qty}</td><td class="cust">${v.customers.map(esc).join(", ")}</td></tr>`).join("");
  const slips = data.by_customer.map((c) => `
    <div class="slip">
      <div class="slip-head">
        <strong>${esc(c.name)}</strong>
        <span>${esc(c.email)}</span>
        ${c.order_number ? `<span>Order #${esc(c.order_number)}</span>` : ""}
        <span class="pay ${c.payment_status === "paid" ? "paid" : "unpaid"}">${esc(c.payment_status)}</span>
        ${c.round > 1 ? `<span class="round">waitlist round</span>` : ""}
      </div>
      <ul>${c.items.map((i) => `<li>☐ ${esc(i.name)} <em>(${esc(i.tier)})</em></li>`).join("")}</ul>
      <div class="slip-total">${c.items.length} tuber(s) · NZ$${Number(c.total).toFixed(2)}</div>
    </div>`).join("");
  const html = `<!doctype html><html><head><title>Picking list , ${esc(data.release.name)}</title><style>
    body{font-family:Georgia,'Times New Roman',serif;color:#3E2346;margin:32px;}
    h1{font-size:22px;margin:0;} h2{font-size:15px;letter-spacing:.2em;text-transform:uppercase;margin:28px 0 10px;border-bottom:2px solid #C9A268;padding-bottom:4px;}
    .meta{font-size:12px;opacity:.7;margin-top:4px;}
    table{width:100%;border-collapse:collapse;font-size:13px;}
    td,th{border:1px solid #E5D9C5;padding:6px 8px;text-align:left;vertical-align:top;}
    .qty{width:50px;text-align:center;font-weight:bold;} .cust{font-size:11px;opacity:.8;}
    .tier{font-size:10px;background:#EFE4CC;border-radius:8px;padding:1px 6px;margin-left:4px;}
    .slip{border:1px solid #E5D9C5;border-radius:6px;padding:10px 12px;margin-bottom:10px;page-break-inside:avoid;}
    .slip-head{display:flex;flex-wrap:wrap;gap:10px;font-size:12px;align-items:baseline;}
    .slip ul{margin:6px 0;padding-left:4px;list-style:none;font-size:13px;line-height:1.7;}
    .slip-total{font-size:11px;opacity:.7;}
    .pay{font-size:10px;border-radius:8px;padding:1px 8px;text-transform:uppercase;letter-spacing:.1em;}
    .pay.paid{background:#E8EFE2;color:#3F5F2F;} .pay.unpaid{background:#FBF0E0;color:#A05A20;}
    .round{font-size:10px;background:#F2EAF3;color:#6E1E3A;border-radius:8px;padding:1px 8px;}
    @media print{.noprint{display:none;}}
  </style></head><body>
    <div class="noprint" style="text-align:right"><button onclick="window.print()" style="padding:8px 18px;font-size:13px;cursor:pointer;">Print</button></div>
    <h1>Fairitage Picking List , ${esc(data.release.name)}</h1>
    <div class="meta">${data.total_tubers} tubers across ${data.by_customer.length} customers · generated ${new Date().toLocaleString()}</div>
    <h2>Pull by variety</h2>
    <table><tr><th>Variety</th><th class="qty">Qty</th><th>Goes to</th></tr>${varietyRows}</table>
    <h2>Pack by customer</h2>
    ${slips}
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}
